import { useState, useEffect, useCallback, useRef } from 'react'
import { learningService } from '../services/learningService'
import { LearningVideo, VideoProgress } from '../types/learning'

interface VideoPlayerState {
  currentTime: number
  duration: number
  isPlaying: boolean
  volume: number
  playbackRate: number
  progress: VideoProgress | null
  loading: boolean
  error: string | null
}

export const useVideoPlayer = (video: LearningVideo | null, userId?: string) => {
  const [state, setState] = useState<VideoPlayerState>({
    currentTime: 0,
    duration: 0,
    isPlaying: false,
    volume: 1,
    playbackRate: 1,
    progress: null,
    loading: false,
    error: null
  })

  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastSavedTimeRef = useRef<number>(0)

  // Update video progress in database every 10 seconds
  const updateProgress = useCallback(async (currentTime: number) => {
    if (!video || !userId) return

    try {
      const watchedSeconds = Math.round(currentTime)
      
      // Only update if we've watched at least 10 more seconds since last save
      if (watchedSeconds - lastSavedTimeRef.current >= 10) {
        await learningService.updateVideoProgress({
          video_id: video.id,
          watched_seconds: watchedSeconds,
          completed: watchedSeconds >= (video.duration || 0) * 0.9 // 90% watched = complete
        })
        
        lastSavedTimeRef.current = watchedSeconds
        
        // Update local progress state
        setState(prev => ({
          ...prev,
          progress: prev.progress ? {
            ...prev.progress,
            watched_seconds: watchedSeconds,
            completion_percentage: video.duration 
              ? Math.min(100, Math.round((watchedSeconds / video.duration) * 100))
              : 0,
            time_remaining: video.duration 
              ? Math.max(0, video.duration - watchedSeconds)
              : 0
          } : null
        }))
      }
    } catch (error) {
      console.error('Error updating video progress:', error)
    }
  }, [video, userId])

  // Start progress tracking
  const startProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
    }

    progressIntervalRef.current = setInterval(() => {
      updateProgress(state.currentTime)
    }, 10000) // Update every 10 seconds
  }, [updateProgress, state.currentTime])

  // Stop progress tracking
  const stopProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }
  }, [])

  // Initialize progress tracking
  useEffect(() => {
    if (video && userId) {
      // Set initial progress from video data
      if (video.progress) {
        setState(prev => ({
          ...prev,
          progress: video.progress || null,
          currentTime: video.progress?.watched_seconds || 0
        }))
        lastSavedTimeRef.current = video.progress?.watched_seconds || 0
      }
    }
  }, [video, userId])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopProgressTracking()
    }
  }, [stopProgressTracking])

  // Video player controls
  const play = useCallback(() => {
    setState(prev => ({ ...prev, isPlaying: true }))
    startProgressTracking()
  }, [startProgressTracking])

  const pause = useCallback(() => {
    setState(prev => ({ ...prev, isPlaying: false }))
    stopProgressTracking()
    // Save progress immediately when pausing
    if (video && userId) {
      updateProgress(state.currentTime)
    }
  }, [stopProgressTracking, updateProgress, state.currentTime, video, userId])

  const seekTo = useCallback((time: number) => {
    setState(prev => ({ ...prev, currentTime: time }))
  }, [])

  const setVolume = useCallback((volume: number) => {
    setState(prev => ({ ...prev, volume: Math.max(0, Math.min(1, volume)) }))
  }, [])

  const setPlaybackRate = useCallback((rate: number) => {
    setState(prev => ({ ...prev, playbackRate: rate }))
  }, [])

  const setDuration = useCallback((duration: number) => {
    setState(prev => ({ ...prev, duration }))
  }, [])

  const setCurrentTime = useCallback((currentTime: number) => {
    setState(prev => ({ ...prev, currentTime }))
  }, [])

  // Mark video as complete
  const markComplete = useCallback(async () => {
    if (!video || !userId) return

    try {
      setState(prev => ({ ...prev, loading: true }))
      
      await learningService.markVideoComplete(userId, video.id)
      
      setState(prev => ({
        ...prev,
        loading: false,
        progress: prev.progress ? {
          ...prev.progress,
          completed: true,
          completed_at: new Date().toISOString(),
          completion_percentage: 100,
          time_remaining: 0
        } : null
      }))
    } catch (error) {
      console.error('Error marking video complete:', error)
      setState(prev => ({ ...prev, loading: false, error: 'Failed to mark video complete' }))
    }
  }, [video, userId])

  // Calculate completion percentage
  const completionPercentage = video?.duration 
    ? Math.min(100, Math.round((state.currentTime / video.duration) * 100))
    : 0

  // Check if video should be marked complete (90% watched)
  const shouldMarkComplete = completionPercentage >= 90 && !state.progress?.completed

  // Auto-mark complete when threshold is reached
  useEffect(() => {
    if (shouldMarkComplete && video && userId) {
      markComplete()
    }
  }, [shouldMarkComplete, markComplete, video, userId])

  return {
    // State
    currentTime: state.currentTime,
    duration: state.duration,
    isPlaying: state.isPlaying,
    volume: state.volume,
    playbackRate: state.playbackRate,
    progress: state.progress,
    loading: state.loading,
    error: state.error,
    completionPercentage,
    
    // Actions
    play,
    pause,
    seekTo,
    setVolume,
    setPlaybackRate,
    setDuration,
    setCurrentTime,
    markComplete,
    
    // Utilities
    formatTime: (seconds: number) => {
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },
    
    timeRemaining: video?.duration 
      ? Math.max(0, video.duration - state.currentTime)
      : 0
  }
}
